import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewRef,
  inject,
} from '@angular/core';
import { AvailableLanguages, SUPPORTED_LANGS } from '@constants';
import {
  DISCOUNT_CODE_TYPE,
  DiscountCode,
  PARTNER_POINTS_STATUS,
  PARTNER_TERMS_CONFIG,
  ROLES_KEYS,
  SumOfPartnerPoints,
  User,
} from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalService } from '@shared/confirmation-modal/confirmation-modal.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { MyPartnerAccountFacade } from '@state/my-partner-account';
import { UserAuthFacade } from '@state/user-auth';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { combineLatest, lastValueFrom, map, ReplaySubject, take, takeUntil } from 'rxjs';
import { PayoutRequestModalService } from './payout-request-modal/payout-request-modal.service';

@Component({
  selector: 'app-my-partner-account-drawer',
  templateUrl: './my-partner-account-drawer.component.html',
  styleUrls: ['./my-partner-account-drawer.component.scss'],
  standalone: false,
})
export class MyPartnerAccountDrawerComponent implements OnInit, OnDestroy {
  private readonly drawerRef = inject<NzDrawerRef<any>>(NzDrawerRef);
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly myPartnerAccountFacade = inject(MyPartnerAccountFacade);
  private readonly confirmation = inject(ConfirmationModalService);
  private readonly snackService = inject(StackedMessagesService);
  private readonly clipboard = inject(Clipboard);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly deviceInfo = inject(DeviceInfoService);
  private readonly payoutRequestModal = inject(PayoutRequestModalService);

  private readonly destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Input() userName: string;

  @ViewChild('circle1') circle1Element: ElementRef<HTMLElement>;
  @ViewChild('circle2') circle2Element: ElementRef<HTMLElement>;
  @ViewChild('circle3') circle3Element: ElementRef<HTMLElement>;

  public myself: User;

  public partnerCode: Partial<DiscountCode>;
  public sumOfPartnerPoints: SumOfPartnerPoints;

  public loadingPartnerCode$ = this.myPartnerAccountFacade.loading$;
  public payoutAvailable$ = this.myPartnerAccountFacade.payoutAvailable$;
  public payoutsHistory$ = this.myPartnerAccountFacade.payoutsHistory$;
  public payoutsHistoryLoading: boolean;

  public disablingLoading$ = combineLatest([this.loadingPartnerCode$]).pipe(
    map(([loadingPartnerCode]) => loadingPartnerCode),
    takeUntil(this.destroy$)
  );

  public loadingView: boolean;

  public DISCOUNT_CODE_TYPE = DISCOUNT_CODE_TYPE;
  public PARTNER_TERMS_CONFIG = PARTNER_TERMS_CONFIG;
  public PARTNER_POINTS_STATUS = PARTNER_POINTS_STATUS;
  public SUPPORTED_LANGS = SUPPORTED_LANGS;

  public userLang: AvailableLanguages;
  public hoveredOnSumCategory: {
    categoryName: PARTNER_POINTS_STATUS;
    pointsSum: number;
  } = null;

  public instructionsDrawerVisible = false;
  public codeUsageDrawerVisible = false;
  public payoutsHistoryDrawerVisible = false;
  public isDesktop: boolean;
  public payoutAllowed: boolean;
  public title: string;

  public async ngOnInit(): Promise<void> {
    this.userLang = this.translateService.currentLang as AvailableLanguages;
    this.isDesktop = this.deviceInfo.getDeviceType() === 'DESKTOP';

    this.loadingView = true;

    try {
      this.myself = await lastValueFrom(this.userAuthFacade.myself$.pipe(take(1), takeUntil(this.destroy$)));

      const rolesAllowedToParticipate = [ROLES_KEYS.Foreigner];
      const userRole = this.myself.role.key as ROLES_KEYS;
      const allowedToParticipateInPartnerProgram =
        rolesAllowedToParticipate.includes(userRole) && this.myself.company?.purchaseEnabled;

      if (!allowedToParticipateInPartnerProgram) {
        this.drawerRef.close();
        console.error('NOT_ALLOWED_TO_PARTICIPATE');
        return;
      }

      await this.initData();
      this.title = this.translateService.instant('PARTNER_ACCOUNT.YOUR_PARTNER_ACCOUNT');
    } finally {
      this.loadingView = false;

      this.cdr.markForCheck();
    }
  }

  private async initData(): Promise<void> {
    const [partnerCode, sumOfPartnerPoints] = await Promise.all([
      this.myPartnerAccountFacade.getMyPartnerAccountDetails$(),
      this.myPartnerAccountFacade.getMyPointsSum$(),
    ]);

    this.partnerCode = partnerCode;
    this.sumOfPartnerPoints = sumOfPartnerPoints;
    if (sumOfPartnerPoints?.GRANTED > 0) {
      this.myPartnerAccountFacade.isPayoutAvailable();
    }
  }

  public copyToClipboard(text: string): void {
    if (!text?.length || !text.trim().length) {
      this.snackService.showError({ message: 'COMMON.CANNOT_COPY_EMPTY_TEXT_TO_CLIPBOARD' });
      return;
    }

    const success = this.clipboard.copy(text);
    let trimmedText = text.trim();
    if (trimmedText.length > 20) {
      trimmedText = `${text.slice(0, 20)}...`;
    }

    if (success) {
      this.snackService.showInfo({
        message: 'COMMON.NOTIFICATION.COPIED_TO_CLIPBOARD',
        translateParams: { text: trimmedText },
      });
    }
    if (!success) {
      this.snackService.showError({
        message: 'COMMON.NOTIFICATION.UNABLE_TO_COPY_TO_CLIPBOARD',
        translateParams: { text: trimmedText },
      });
    }
  }

  public onCategoryHover(category: PARTNER_POINTS_STATUS): void {
    setTimeout(() => {
      this.hoveredOnSumCategory = {
        categoryName: category,
        pointsSum: this.sumOfPartnerPoints[category],
      };
    }, 150);
  }

  public openPayoutRequestModal(): void {
    this.payoutRequestModal
      .open({
        userName: this.userName,
        userPoints: this.sumOfPartnerPoints.GRANTED,
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(async requestMade => {
        if (!requestMade) {
          // do nothing
          return;
        }

        this.loadingView = true;

        try {
          await this.initData();
        } finally {
          this.loadingView = false;
          this.cdr.markForCheck();
        }
      });
  }

  public async openPointsHistoryDrawer(): Promise<void> {
    try {
      this.payoutsHistoryLoading = true;
      this.payoutsHistoryDrawerVisible = true;
      await this.myPartnerAccountFacade.getMyPayoutsHistory();
    } catch {
      this.snackService.showError({ message: 'PARTNER_ACCOUNT.ERROR_REQUESTING_PAYOUTS_HISTORY' });
      this.payoutsHistoryDrawerVisible = false;
    } finally {
      this.payoutsHistoryLoading = false;

      const viewRef = this.cdr as ViewRef;
      if (!viewRef.destroyed) {
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.drawerRef.close();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public close(): void {
    this.drawerRef.close();
  }
}
